import React, { useCallback, useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"

import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { usePromotion } from "extensions/iconsiam/hooks/promotion"
import { usePageSideBarLayout } from "providers/ui/hooks"

import { PageCard, PageCardContent } from "components/card"
import { PercentIcon } from "components/icons"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { Carousel, CarouselItem } from "components/Carousel"
import { EmptyData } from "components/EmptyData"
import { SidebarMenu } from "extensions/iconsiam/components/navigation/kiosk-h/SidebarMenu"
import { ContentWithAside } from "layouts/kiosk-h"
import { useVenue } from "providers/venue"
import { VenueSelector } from "components/VenueSelector"
import { PromotionCard } from "extensions/iconsiam/components/domains/promotions"

export const PromotionsKioskHPage = ({
  Layout = ContentWithAside,
  AppNavigation = DefaultAppNavigation,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { changeVenueFilter, locationVenueId, venueFilter, promotions } =
    usePromotion()
  const { venues, isMultiVenueProject } = useVenue()
  const theme = useTheme()
  const slidePerPage = 3
  const carouselType =
    promotions && promotions.length < slidePerPage ? "slide" : "loop"
  const isAutoplay = promotions && promotions.length > slidePerPage

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )
  const { styleOverrides } = theme.components?.[`PromotionPage`] || {}
  const { promotionCarouselOption } =
    theme.components[`PromotionPage`]?.defaultProps || {}
  const {
    pageCard,
    pageCardContent,
    carouselContainer,
    carousel,
    emptyDataContainer,
    venueSelector,
  } = styleOverrides

  const handleClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigate(slide.getAttribute("link"))
    },
    [navigate]
  )

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  const direction = theme.direction

  return (
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent sx={pageCardContent}>
          {!promotions || promotions.length <= 0 ? (
            <Box sx={emptyDataContainer}>
              <EmptyData
                icon={emptyIcon}
                primary={t("No Current Promotions")}
                secondary={t("Check Back Soon")}
              />
            </Box>
          ) : (
            <Box sx={carouselContainer}>
              <Carousel
                onClick={handleClickSlide}
                options={{
                  type: carouselType,
                  perPage: slidePerPage,
                  pagination: true,
                  trimSpace: false,
                  autoplay: isAutoplay,
                  gap: "24px",
                  focus: "center",
                  direction: direction,
                  ...promotionCarouselOption,
                }}
                sx={carousel}
              >
                {promotions.map((promotion) => {
                  const sponsoredId = promotion?.properties?.sponsoredId || ""
                  return (
                    <CarouselItem
                      link={`/promotions/${promotion.id}`}
                      key={`${sponsoredId}${promotion.id}`}
                    >
                      <PromotionCard promotion={promotion} />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            </Box>
          )}
          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={changeVenueFilter}
                currentVenue={locationVenueId}
              />
            </Box>
          )}
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
